import { Dispatch, ReactNode, SetStateAction, useCallback, useMemo } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProps,
  DroppableProvided,
  DropResult,
} from '@hello-pangea/dnd';
import { alpha, Box, BoxProps, Stack, StackProps, styled } from '@mui/material';
import classNames from 'classnames';
import { v4 } from 'uuid';

import { DraggableContext } from '@components/base';

type SortableListProps<T> = Pick<DroppableProps, 'direction'> & {
  ContainerProps?: Omit<StackProps, 'direction'>;
  disabled?: boolean;
  getItemId?: (item: T, index: number) => string;
  indicateOnDrag?: boolean;
  items: T[];
  itemWrapperProps?: BoxProps;
  renderItem: (item: T, index: number) => ReactNode;
  setItems: Dispatch<SetStateAction<T[]>>;
};

const DraggableContainer = styled(Box)`
  &.dragging {
    &.indicate {
      background: ${({ theme }) =>
        alpha(theme.palette.background.default, theme.palette.action.disabledOpacity)};
      box-shadow: ${({ theme }) => theme.shadows[5]};
    }

    .hide-on-drag {
      opacity: 0;
    }
  }
`;

const DroppableContainer = styled(Stack)`
  overflow: auto;
`;

// Helper function to generate stable IDs without expensive JSON.stringify
const getItemId = <T,>(item: T, index: number): string => {
  // Try to use item's own id/slug if available, otherwise use index
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    if (obj.id) return String(obj.id);
    if (obj.slug) return String(obj.slug);
    if (obj.sourceId) return String(obj.sourceId);
  }
  return `item-${index}`;
};

export function SortableList<T>({
  direction,
  disabled,
  getItemId: getItemIdProp,
  itemWrapperProps,
  items,
  indicateOnDrag,
  ContainerProps: containerProps,
  renderItem,
  setItems,
}: SortableListProps<T>) {
  const handleDragEnd = useCallback(
    (res: DropResult) => {
      const { destination, source } = res;
      if (!destination || destination.index === source.index) return;

      setItems(prev => {
        const arr = [...prev];

        if (destination.index >= arr.length) {
          let k = destination.index - arr.length + 1;
          while (k--) arr.push({} as T);
        }

        arr.splice(destination.index, 0, arr.splice(source.index, 1)[0]);

        return arr;
      });
    },
    [setItems]
  );

  const droppableId = useMemo(() => v4(), []);

  const moveToBack = useCallback(
    (index: number) =>
      handleDragEnd({
        destination: { droppableId, index: items.length - 1 },
        source: { droppableId, index },
      } as DropResult),
    [handleDragEnd, droppableId, items.length]
  );

  const moveToFront = useCallback(
    (index: number) =>
      handleDragEnd({
        destination: { droppableId, index: 0 },
        source: { droppableId, index },
      } as DropResult),
    [handleDragEnd, droppableId]
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable direction={direction} droppableId={droppableId}>
        {(droppableProvided: DroppableProvided) => (
          <DroppableContainer
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            {...containerProps}
            className={classNames(containerProps?.className, direction)}
            direction={direction === 'horizontal' ? 'row' : 'column'}
          >
            {items.map((item, index) => {
              const itemId = getItemIdProp?.(item, index) ?? getItemId(item, index);
              return (
                <Draggable
                  draggableId={itemId}
                  index={index}
                  isDragDisabled={disabled}
                  key={itemId}
                >
                  {(draggableProvided, snapshot) => (
                    <DraggableContext.Provider
                      value={{
                        draggableProvided,
                        isDragging: snapshot.isDragging,
                        moveToBack: () => moveToBack(index),
                        moveToFront: () => moveToFront(index),
                        snapshot,
                      }}
                    >
                      <DraggableContainer
                        {...draggableProvided.draggableProps}
                        {...itemWrapperProps}
                        className={classNames(itemWrapperProps?.className, {
                          dragging: snapshot.isDragging,
                          indicate: indicateOnDrag,
                        })}
                        ref={draggableProvided.innerRef}
                      >
                        {renderItem(item, index)}
                      </DraggableContainer>
                    </DraggableContext.Provider>
                  )}
                </Draggable>
              );
            })}

            {droppableProvided.placeholder}
          </DroppableContainer>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default SortableList;
